import { defineStore, type StateTree } from 'pinia';
import { cloneDeep } from 'lodash-es';
import { useSiteConfigStore } from '../use-site-config-store';
import type {
  EditableField,
  FailureClass,
  GuestDetail,
  GuestListEntry,
  GuestListPage,
  MealPlanHealthSummary,
} from './types';

interface State extends StateTree {
  summary: MealPlanHealthSummary | null;
  guests: GuestListEntry[];
  selectedGuest: GuestDetail | null;
  guestsTruncated: boolean;
  windowHours: number;
  search: string;
  failureClass: FailureClass | null;
}

const DEFAULT_WINDOW_HOURS = 168;

/** The meal plan configuration fields this page may write, and where each one lives. */
export const EDITABLE_FIELDS: Record<string, EditableField> = {
  terminal_id: { scope: 'public', kind: 'string' },
  valid_email_domains: { scope: 'public', kind: 'list' },
  'keystore.certificate.password': { scope: 'private', kind: 'string' },
};

/** Describes a field this page is allowed to write, or `undefined` when the service named a field it does not edit. */
export function editableField(field: string | undefined): EditableField | undefined {
  return field ? EDITABLE_FIELDS[field] : undefined;
}

/** A field names no meal plan, so it is only unambiguous while the site has one. */
function singleEntry<T>(entries: T[]): T {
  if (!entries.length) throw new Error('No meal plan configured for this site');
  if (entries.length > 1) {
    throw new Error('This site has several meal plans; edit the one you mean in the form');
  }
  return entries[0];
}

function setByPath(entry: object, path: string, value: string | string[]): void {
  const segments = path.split('.');
  const leaf = segments.pop() as string;

  const target = segments.reduce<Record<string, unknown>>(
    (node, segment) => {
      if (typeof node[segment] !== 'object' || node[segment] === null) {
        node[segment] = {};
      }
      return node[segment] as Record<string, unknown>;
    },
    entry as Record<string, unknown>,
  );

  target[leaf] = value;
}

function getByPath(entry: object, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (node, segment) =>
        typeof node === 'object' && node !== null
          ? (node as Record<string, unknown>)[segment]
          : undefined,
      entry,
    );
}

export function useMealPlanHealthStore(storeId = 'mealPlanHealth') {
  const store = defineStore(storeId, {
    state: (): State => ({
      summary: null,
      guests: [],
      selectedGuest: null,
      guestsTruncated: false,
      windowHours: DEFAULT_WINDOW_HOURS,
      search: '',
      failureClass: null,
    }),

    getters: {
      actionableFindings: (state) =>
        (state.summary?.findings ?? []).filter((finding) => finding.counts_toward_health),

      informationalFindings: (state) =>
        (state.summary?.findings ?? []).filter((finding) => !finding.counts_toward_health),

      provider: (state) => state.summary?.provider ?? 'the provider',
    },

    actions: {
      /** The meal plan id the health routes take, which the site's configuration holds. */
      async mealplanId(siteId: string): Promise<string> {
        const siteConfigStore = useSiteConfigStore();

        if (!siteConfigStore.sitePublicConfig) {
          await siteConfigStore.fetchSiteConfigurations(siteId);
        }

        return siteConfigStore.sitePublicConfig?.mealplan?.[0]?.id ?? siteId;
      },

      async fetchSummary(siteId: string): Promise<void> {
        const id = await this.mealplanId(siteId);

        const response = await this.cdlApi.get<MealPlanHealthSummary>(`/mealplan/${id}/health`, {
          params: { windowHours: this.windowHours },
        });
        this.summary = response?.data ?? null;
      },

      async fetchGuests(siteId: string): Promise<void> {
        const id = await this.mealplanId(siteId);

        const response = await this.cdlApi.get<GuestListPage>(`/mealplan/${id}/health/guest`, {
          params: {
            windowHours: this.windowHours,
            search: this.search || undefined,
            failureClass: this.failureClass ?? undefined,
          },
        });
        this.guests = response?.data?.guests ?? [];
        this.guestsTruncated = response?.data?.truncated ?? false;
      },

      async fetchGuest(siteId: string, userId: string): Promise<void> {
        const id = await this.mealplanId(siteId);

        const response = await this.cdlApi.get<GuestDetail>(
          `/mealplan/${id}/health/guest/${userId}`,
          { params: { windowHours: this.windowHours } },
        );
        this.selectedGuest = response?.data ?? null;
      },

      /** The list a field holds today, so an edit adds to it rather than replacing it. */
      fieldList(field: string): string[] {
        const spec = editableField(field);
        if (spec?.kind !== 'list') return [];

        const siteConfigStore = useSiteConfigStore();
        const config =
          spec.scope === 'private'
            ? siteConfigStore.sitePrivateConfig
            : siteConfigStore.sitePublicConfig;
        const entries = config?.mealplan ?? [];
        if (entries.length !== 1) return [];

        const value = getByPath(entries[0], field);
        return Array.isArray(value) ? value.map(String) : [];
      },

      /** Writes one allowlisted field into the site's meal plan configuration. */
      async saveConfigField(
        siteId: string,
        field: string,
        value: string | string[],
      ): Promise<void> {
        const spec = editableField(field);
        if (!spec) throw new Error(`${field} cannot be edited from the meal plan health page`);

        if (spec.kind === 'list' && !Array.isArray(value)) {
          throw new Error(`${field} holds a list of values`);
        }
        if (spec.kind === 'string' && typeof value !== 'string') {
          throw new Error(`${field} holds a single value`);
        }

        const siteConfigStore = useSiteConfigStore();

        if (spec.scope === 'private') {
          const config = cloneDeep(siteConfigStore.sitePrivateConfig);
          if (!config) throw new Error('No meal plan configured for this site');

          setByPath(singleEntry(config.mealplan ?? []), field, value);
          await siteConfigStore.savePrivate(siteId, config);
        } else {
          const config = cloneDeep(siteConfigStore.sitePublicConfig);
          if (!config) throw new Error('No meal plan configured for this site');

          setByPath(singleEntry(config.mealplan ?? []), field, value);
          await siteConfigStore.savePublic(siteId, config);
        }

        await siteConfigStore.fetchSiteConfigurations(siteId);
      },

      async saveTenderNumbers(siteId: string, numbers: string[]): Promise<void> {
        const siteConfigStore = useSiteConfigStore();

        const config = cloneDeep(siteConfigStore.sitePublicConfig);
        if (!config) throw new Error('No meal plan configured for this site');

        const entry = singleEntry(config.mealplan ?? []);
        const existing = entry.tenders ?? [];
        const byId = new Map(existing.map((tender) => [String(tender.id), tender]));
        const defaultType = existing[0]?.type;

        entry.tenders = numbers.map((id) => byId.get(id) ?? { id, type: defaultType, name: id });

        await siteConfigStore.savePublic(siteId, config);
        await siteConfigStore.fetchSiteConfigurations(siteId);
      },

      clearSelectedGuest(): void {
        this.selectedGuest = null;
      },
    },
  });

  return store();
}
